// src/components/ProjectManagement/ProjectManagementDashboard.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth } from '../../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, onSnapshot, doc, getDoc, addDoc, setDoc, getDocs } from 'firebase/firestore';
import KanbanBoard from './KanbanBoard';
import WorkerDashboard from './WorkerDashboard';
import QCTaskVerification from './QCTaskVerification';
import LoadBalancingView from './LoadBalancingView';
import NotificationBell from './NotificationBell';
import ProjectProgress from './ProjectProgress';
import DataAppraisal from './DataAppraisal';
import CreateProjectModal from './CreateProjectModal';
import ProjectBrowserModal from './ProjectBrowserModal';
import ChangeProjectEndDateModal from './ChangeProjectEndDateModal';
import EditProjectModal from './EditProjectModal';
import ClientsManagement from './ClientsManagement';
import '../../styles/projectManagement.css';
import ProjectPlanner from './ProjectPlanner';
import ClientView from './ClientView';

const ProjectManagementDashboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState('my-tasks');
  const [selectedProjectId, setSelectedProjectId] = useState('all');
  const [projects, setProjects] = useState([]);          // raw projects from Firestore (no progress)
  const [tasksCache, setTasksCache] = useState({});      // { projectId: [task, ...] }
  const [clients, setClients] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBrowserModal, setShowBrowserModal] = useState(false);
  const [showChangeEndDateModal, setShowChangeEndDateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  
  // Filter states for CEO/Manager
  const [globalFilterProjectId, setGlobalFilterProjectId] = useState(null);
  const [globalFilterProjectName, setGlobalFilterProjectName] = useState(null);

  // ─── 1. Auth listener ──────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserRole(data?.role || 'worker');
            setUserData(data);
          } else {
            await setDoc(userDocRef, {
              name: user.email?.split('@')[0] || 'User',
              email: user.email,
              role: 'worker',
              createdAt: new Date()
            });
            setUserRole('worker');
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          setUserRole('worker');
        }
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // ─── 2. Listen to projects (REAL‑TIME, but no nested reads) ──────
  useEffect(() => {
    if (!currentUser) return;
    const managerRoles = ['manager', 'ceo', 'workshop-manager', 'managing-director'];
    if (!managerRoles.includes(userRole) && userRole !== 'quality-control') {
      return;
    }

    const projectsUnsubscribe = onSnapshot(
      collection(db, 'projects'),
      (snapshot) => {
        const rawProjects = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProjects(rawProjects);
      },
      (error) => console.error('Projects snapshot error:', error)
    );

    return () => projectsUnsubscribe();
  }, [currentUser, userRole, refreshKey]);

  // ─── 3. Listen to ALL tasks (REAL‑TIME) and build cache ──────────
  useEffect(() => {
    if (!currentUser) return;
    // Only needed for roles that display progress (manager/QC)
    const managerRoles = ['manager', 'ceo', 'workshop-manager', 'managing-director'];
    if (!managerRoles.includes(userRole) && userRole !== 'quality-control') {
      return;
    }

    const tasksUnsubscribe = onSnapshot(
      collection(db, 'tasks'),
      (snapshot) => {
        const newCache = {};
        snapshot.docs.forEach(doc => {
          const task = doc.data();
          const pid = task.projectId;
          if (pid) {
            if (!newCache[pid]) newCache[pid] = [];
            newCache[pid].push(task);
          }
        });
        setTasksCache(newCache);
      },
      (error) => console.error('Tasks snapshot error:', error)
    );

    return () => tasksUnsubscribe();
  }, [currentUser, userRole]);

  // ─── 4. Compute projects with progress and sort (memoized) ──────
  const projectsWithProgress = useMemo(() => {
    return projects
      .map(project => {
        const tasks = tasksCache[project.id] || [];
        const completed = tasks.filter(t => t.status === 'completed' && t.qcVerified === true).length;
        const progress = tasks.length > 0 ? Math.round((completed / tasks.length) * 100) : 0;
        return {
          ...project,
          progress,
          totalTasks: tasks.length,
          completedTasks: completed
        };
      })
      .sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dateB - dateA;
      });
  }, [projects, tasksCache]);

  // ─── 5. Fetch client details in batches (no sequential reads) ────
  useEffect(() => {
    const fetchClients = async () => {
      if (projectsWithProgress.length === 0) {
        setClients({});
        return;
      }
      const clientIds = [
        ...new Set(
          projectsWithProgress.map(p => p.clientId).filter(Boolean)
        )
      ];
      if (clientIds.length === 0) {
        setClients({});
        return;
      }

      const clientMap = {};
      // Firestore 'in' queries support up to 10 IDs – chunk them
      const chunkSize = 10;
      for (let i = 0; i < clientIds.length; i += chunkSize) {
        const chunk = clientIds.slice(i, i + chunkSize);
        const q = query(collection(db, 'clients'), where('__name__', 'in', chunk));
        const snap = await getDocs(q);
        snap.docs.forEach(doc => {
          clientMap[doc.id] = { id: doc.id, ...doc.data() };
        });
      }
      setClients(clientMap);
    };

    fetchClients();
  }, [projectsWithProgress]);

  // ─── 6. Role helpers ──────────────────────────────────────────────
  const isManager = ['manager', 'ceo', 'managing-director'].includes(userRole);
  const isWorker = ['carpenter', 'electrician','workshop-manager', 'machine-operator', 'assistant-carpenter', 'wood-polisher', 'distributor', 'designer', 'marketing-sales'].includes(userRole);
  const isQC = userRole === 'quality-control';
  const canHaveTasks = isManager || isQC || isWorker;
  const canEdit = isManager || isQC;
  const canViewClients = isManager;

  // ─── 7. Set default tab based on role ─────────────────────────────
  useEffect(() => {
    if (userRole) {
      if (isWorker) {
        setActiveTab('my-tasks');
      } else if (isQC) {
        setActiveTab('qc-verification');
      } else if (isManager) {
        setActiveTab('kanban');
      }
    }
  }, [userRole, isWorker, isQC, isManager]);

  // ─── 8. Handlers (unchanged) ──────────────────────────────────────
  const handleCreateProject = (projectData) => {
    console.log('Project created by modal:', projectData);
    setShowCreateModal(false);
  };

  const handleSelectProject = (project) => {
    setSelectedProjectId(project.id);
    setShowBrowserModal(false);
    setGlobalFilterProjectId(null);
    setGlobalFilterProjectName(null);
  };

  const handleFilterByProject = (projectId, projectName) => {
    console.log('Dashboard filtering by project:', projectId, projectName);
    setGlobalFilterProjectId(projectId);
    setGlobalFilterProjectName(projectName);
  };

  const clearGlobalFilter = () => {
    console.log('Clearing global filter');
    setGlobalFilterProjectId(null);
    setGlobalFilterProjectName(null);
    setSelectedProjectId('all');
  };

  const handleProjectEndDateChanged = () => {
    setRefreshKey(prev => prev + 1);
    setShowChangeEndDateModal(false);
  };

  // ─── 9. Memoized selected project/client ──────────────────────────
  const selectedProject = useMemo(() => {
    if (selectedProjectId === 'all') return null;
    return projectsWithProgress.find(p => p.id === selectedProjectId) || null;
  }, [projectsWithProgress, selectedProjectId]);

  const selectedClient = useMemo(() => {
    if (!selectedProject?.clientId) return null;
    return clients[selectedProject.clientId] || null;
  }, [selectedProject, clients]);

  // ─── 10. Render content (unchanged logic) ─────────────────────────
  const renderContent = useCallback(() => {
    if (isWorker) {
      return <WorkerDashboard currentUser={currentUser} userRole={userRole} />;
    }
    
    if (isQC) {
      if (projectsWithProgress.length === 0) {
        return (
          <div className="empty-state">
            <div className="empty-state-icon">🔍</div>
            <h3>No Projects Available</h3>
            <p>Projects will appear here once created</p>
          </div>
        );
      }
      
      switch (activeTab) {
        case 'my-tasks':
          return <WorkerDashboard currentUser={currentUser} userRole={userRole} />;
        case 'qc-verification':
          return <QCTaskVerification
            currentUser={currentUser}
            userRole={userRole}
            projectId={selectedProjectId === 'all' ? null : selectedProjectId}
          />;
        case 'kanban':
          return <KanbanBoard
            projectId={selectedProjectId === 'all' ? null : selectedProjectId}
            currentUser={currentUser}
            userRole={userRole}
            onFilterByProject={handleFilterByProject}
            filterProjectId={globalFilterProjectId}
            clearFilter={clearGlobalFilter}
            filterProjectName={globalFilterProjectName}
          />;
        case 'planner':
          return <ProjectPlanner currentUser={currentUser} userRole={userRole} />;
        case 'clientsview':
          return <ClientView currentUser={currentUser} userRole={userRole} />;
        default:
          return <QCTaskVerification
            currentUser={currentUser}
            userRole={userRole}
            projectId={selectedProjectId === 'all' ? null : selectedProjectId}
          />;
      }
    }
    
    if (isManager) {
      if (projectsWithProgress.length === 0) {
        return (
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <h3>No Projects Yet</h3>
            <button className="create-project-btn" onClick={() => setShowCreateModal(true)}>
              + Create New Project
            </button>
          </div>
        );
      }
      
      switch (activeTab) {
        case 'my-tasks':
          return <WorkerDashboard currentUser={currentUser} userRole={userRole} />;
        case 'kanban':
          return <KanbanBoard
            projectId={selectedProjectId === 'all' ? null : selectedProjectId}
            currentUser={currentUser}
            userRole={userRole}
            onFilterByProject={handleFilterByProject}
            filterProjectId={globalFilterProjectId}
            clearFilter={clearGlobalFilter}
            filterProjectName={globalFilterProjectName}
          />;
        case 'load-balancing':
          return <LoadBalancingView currentUser={currentUser} userRole={userRole} />;
        case 'progress':
          return <ProjectProgress projectId={selectedProjectId === 'all' ? null : selectedProjectId} />;
        case 'appraisal':
          return <DataAppraisal currentUser={currentUser} userRole={userRole} />;
        case 'clients':
          return <ClientsManagement currentUser={currentUser} userRole={userRole} />;
        case 'planner':
          return <ProjectPlanner currentUser={currentUser} userRole={userRole} />;
        default:
          return <KanbanBoard
            projectId={selectedProjectId === 'all' ? null : selectedProjectId}
            currentUser={currentUser}
            userRole={userRole}
            onFilterByProject={handleFilterByProject}
            filterProjectId={globalFilterProjectId}
            clearFilter={clearGlobalFilter}
            filterProjectName={globalFilterProjectName}
          />;
      }
    }
    
    return <WorkerDashboard currentUser={currentUser} userRole={userRole} />;
  }, [
    isWorker, isQC, isManager, activeTab, currentUser, userRole,
    projectsWithProgress, selectedProjectId, globalFilterProjectId,
    globalFilterProjectName
  ]);

  // ─── 11. Loading state ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="project-management-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // ─── 12. Render ─────────────────────────────────────────────────────
  return (
    <div className="project-management-container">
      <div className="pm-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>🏭 Kajja Galleries Project Manager</h1>
            <p>Manage tasks, track progress, and coordinate your team efficiently</p>
          </div>
          <NotificationBell userId={currentUser?.uid} userRole={userRole} />
        </div>
        
        <div className="header-stats">
          <div className="stat-badge">
            📊 Active Projects: {projectsWithProgress.filter(p => p.status === 'active').length}
          </div>
          <div className="stat-badge">
            ✅ Completed Projects: {projectsWithProgress.filter(p => p.progress === 100).length}
          </div>
          <div className="stat-badge">
            👥 Role: <strong>{userRole?.toUpperCase() || 'WORKER'}</strong>
          </div>
          <div className="stat-badge">
            👤 Welcome, {userData?.name || currentUser?.email?.split('@')[0] || 'User'}!
          </div>
        </div>
      </div>

      {/* Project Selector Section - Only for managers and QC */}
      {(isManager || isQC) && projectsWithProgress.length > 0 && (
        <>
          <div className="project-selector-bar">
            <div className="project-selector-wrapper">
              <label>📁 Current Project:</label>
              <div className="current-project-display">
                {globalFilterProjectId ? (
                  <>
                    <div className="current-project-info filtered">
                      <span className="current-project-name">🔍 Filtered: {globalFilterProjectName}</span>
                      <span className="current-project-progress">Showing only this project</span>
                    </div>
                    <button className="change-project-btn" onClick={clearGlobalFilter}>
                      ✕ Clear Filter
                    </button>
                  </>
                ) : selectedProject && selectedProjectId !== 'all' ? (
                  <>
                    <div className="current-project-info">
                      <span className="current-project-name">{selectedProject.projectName}</span>
                      <span className="current-project-progress">{selectedProject.progress || 0}% complete</span>
                    </div>
                    <div className="project-action-buttons">
                      <button className="change-project-btn" onClick={() => setShowBrowserModal(true)}>
                        🔍 Change Project
                      </button>
                      {canEdit && (
                        <>
                          <button className="change-end-date-btn" onClick={() => setShowEditProjectModal(true)}>
                            ✏️ Edit Project
                          </button>
                          <button className="change-end-date-btn" onClick={() => setShowChangeEndDateModal(true)}>
                            📅 Change End Date
                          </button>
                        </>
                      )}
                    </div>
                  </>
                ) : selectedProjectId === 'all' ? (
                  <div className="current-project-info all-projects">
                    <span className="current-project-name">📊 All Projects</span>
                    <span className="current-project-progress">Viewing all projects</span>
                  </div>
                ) : (
                  <div className="current-project-info">
                    <span className="current-project-name">No project selected</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="project-actions">
              <button className="filter-projects-btn" onClick={() => setShowBrowserModal(true)}>
                📋 Browse All Projects
              </button>
              {(isManager || isQC) && (
                <button className="create-project-btn-small" onClick={() => setShowCreateModal(true)}>
                  + New Project
                </button>
              )}
            </div>
          </div>

          {/* Client Details Section */}
          {selectedProject && selectedClient && !globalFilterProjectId && (
            <div className="client-details-section">
              <div className="client-details-header">
                <span className="client-icon">👤</span>
                <h3>Client Information</h3>
              </div>
              <div className="client-details-content">
                <div className="client-detail-row">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{selectedClient.name}</span>
                </div>
                <div className="client-detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{selectedClient.phone || 'N/A'}</span>
                </div>
                <div className="client-detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{selectedClient.email || 'N/A'}</span>
                </div>
                <div className="client-detail-row">
                  <span className="detail-label">Location:</span>
                  <span className="detail-value">{selectedClient.location || selectedClient.address || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* All Projects Notice */}
          {selectedProjectId === 'all' && !globalFilterProjectId && (
            <div className="all-projects-notice">
              <span>📊</span>
              <p>Showing tasks from <strong>all projects</strong></p>
            </div>
          )}

          {/* Filter Notice */}
          {globalFilterProjectId && (
            <div className="filter-notice">
              <span>🔍</span>
              <p>Showing tasks from <strong>{globalFilterProjectName}</strong> only</p>
              <button onClick={clearGlobalFilter} className="filter-clear-btn">Clear Filter</button>
            </div>
          )}
        </>
      )}

      {/* Tab Navigation */}
      <div className="pm-tabs">
        {canHaveTasks && (
          <button className={`tab-button ${activeTab === 'my-tasks' ? 'active' : ''}`} onClick={() => setActiveTab('my-tasks')}>
            ✅ My Tasks
          </button>
        )}
        {isQC && (
          <>
            <button className={`tab-button ${activeTab === 'qc-verification' ? 'active' : ''}`} onClick={() => setActiveTab('qc-verification')}>
              🎛️ QC Verification
            </button>
            <button className={`tab-button ${activeTab === 'kanban' ? 'active' : ''}`} onClick={() => setActiveTab('kanban')}>
              📋 Kanban Board
            </button>
            <button className={`tab-button ${activeTab === 'planner' ? 'active' : ''}`} onClick={() => setActiveTab('planner')}>
              📋 Project Planner
            </button>
            {/* <button className={`tab-button ${activeTab === 'clientsview' ? 'active' : ''}`} onClick={() => setActiveTab('clientsview')}>
              👥 Client View
            </button> */}
          </>
        )}
        {isManager && (
          <>
            <button className={`tab-button ${activeTab === 'kanban' ? 'active' : ''}`} onClick={() => setActiveTab('kanban')}>
              📋 Kanban Board
            </button>
            <button className={`tab-button ${activeTab === 'load-balancing' ? 'active' : ''}`} onClick={() => setActiveTab('load-balancing')}>
              ⚖️ Load Balancing
            </button>
            <button className={`tab-button ${activeTab === 'progress' ? 'active' : ''}`} onClick={() => setActiveTab('progress')}>
              📈 Progress Reports
            </button>
            <button className={`tab-button ${activeTab === 'appraisal' ? 'active' : ''}`} onClick={() => setActiveTab('appraisal')}>
              📊 Data Appraisal
            </button>
            <button className={`tab-button ${activeTab === 'clients' ? 'active' : ''}`} onClick={() => setActiveTab('clients')}>
              👥 Clients
            </button>
            <button className={`tab-button ${activeTab === 'planner' ? 'active' : ''}`} onClick={() => setActiveTab('planner')}>
              📋 Project Planner
            </button>
          </>
        )}
      </div>

      <div className="pm-content">
        {renderContent()}
      </div>

      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateProject}
          currentUser={currentUser}
        />
      )}

      <ProjectBrowserModal
        isOpen={showBrowserModal}
        onClose={() => setShowBrowserModal(false)}
        onSelectProject={handleSelectProject}
        currentProjectId={selectedProjectId}
        projects={projectsWithProgress}
      />

      {showChangeEndDateModal && selectedProject && (
        <ChangeProjectEndDateModal
          project={selectedProject}
          currentUser={currentUser}
          onClose={() => setShowChangeEndDateModal(false)}
          onSuccess={handleProjectEndDateChanged}
        />
      )}

      {showEditProjectModal && selectedProject && (
        <EditProjectModal
          project={selectedProject}
          currentUser={currentUser}
          onClose={() => setShowEditProjectModal(false)}
          onSuccess={() => {
            setRefreshKey(prev => prev + 1);
            setShowEditProjectModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ProjectManagementDashboard;